/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { getImageParamsKeyMap } from '@adobe-commerce/elsie/lib/';

const BREAKPOINTS = {
  medium: 768,
  large: 1024,
  xlarge: 1366,
  xxlarge: 1920,
};

export interface ResolveImageUrlOptions {
  width: number;
  height?: number;
  auto?: string;
  quality?: number;
  crop?: boolean;
  fit?: string;
}

const resolveImageUrl = (url: string, _opts?: ResolveImageUrlOptions) => {
  const [base, query] = url.split('?');
  const params = new URLSearchParams(query);

  const keyMapping = getImageParamsKeyMap();
  const keyMappingKeys = (keyMapping && Object.keys(keyMapping)) || [];

  let opts: any = {};
  let unusedMapping = { ...keyMapping };

  if (keyMappingKeys?.length > 0 && _opts) {
    opts = Object.entries(_opts).reduce((acc, [key, value]) => {
      const newKey = keyMapping![key];
      if (typeof newKey === 'string') {
        acc[newKey] = value;
      } else if (typeof newKey === 'function') {
        const [newKeyString, newValue] = newKey(value);
        acc[newKeyString] = newValue;
      }
      delete unusedMapping![key];
      return acc;
    }, {} as { [key: string]: any });
  } else {
    opts = {
      auto: 'webp',
      quality: 80,
      crop: false,
      fit: 'cover',
      ..._opts,
    };
  }

  // Set unused mapping as default params
  Object.entries(unusedMapping).forEach(([, value]) => {
    if (typeof value === 'function') {
      const [newKeyString, newValue] = value(undefined);
      opts[newKeyString] = newValue;
    }
  });

  // Append image optimization parameters
  Object.entries(opts).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.set(key, String(value));
    }
  });

  return `${base}?${params.toString()}`;
};

export const generateSrcset = (
  imageURL: string,
  options: ResolveImageUrlOptions
) => {
  if (!imageURL || !options?.width) return;

  const generateSrcsetUrl = (options: ResolveImageUrlOptions) => {
    return resolveImageUrl(imageURL, {
      ...options,
    });
  };

  return Object.entries(BREAKPOINTS)
    .map(([, value]) => {
      const relativeWidth = Math.round(
        (options.width * value) / BREAKPOINTS.xxlarge
      );

      return `${generateSrcsetUrl({
        ...options,
        width: relativeWidth,
      })} ${relativeWidth}w`;
    })
    .join(',\n');
};
